import { useCallback } from "react";

import useTypedContext from "hooks/useTypedContext";
import useTypedFlags from "hooks/useTypedFlags";
import { AccountContext } from "views/AccountWrapper";
import { NamedWebhooksIntegration } from "types/generated";
import { hasSpaceManageAccess } from "utils/user";
import { hasManageAccess } from "utils/access";

/**
 * Hook that checks if a user has access to manage a webhook.
 *
 * @returns A function that takes a webhook and returns whether the user has access to manage it
 */
const useCanManageWebhook = (): ((webhook?: NamedWebhooksIntegration) => boolean) => {
  const { viewer } = useTypedContext(AccountContext);
  const { customAccessRoles } = useTypedFlags();

  return useCallback(
    (webhook?: NamedWebhooksIntegration) => {
      if (!webhook) {
        return false;
      }

      if (viewer.admin) {
        return true;
      }

      return customAccessRoles
        ? hasManageAccess(viewer, webhook.space.id)
        : hasSpaceManageAccess(webhook.space.accessLevel);
    },
    [customAccessRoles, viewer]
  );
};

export default useCanManageWebhook;
